import { toast } from 'admin/components/common/MessageContainer/toast';

export interface CopyOptions {
	text: string;
	html?: string;
}

const copyLegacy = (text: string): boolean => {
	const el = document.createElement('textarea');
	el.value = text;
	el.setAttribute('readonly', '');
	el.style.cssText = 'position:fixed;opacity:0;pointer-events:none';
	document.body.appendChild(el);
	el.select();

	let copied = false;
	try {
		copied = document.execCommand('copy');
	} catch {
		copied = false;
	}

	document.body.removeChild(el);

	return copied;
};

/**
 * Copy content to clipboard.
 *
 * Usage:
 * - Pass only `text` for plain copy (URLs, IDs, domains, tokens, etc.).
 * - Pass both `text` and `html` when you need rich paste in editors that support HTML.
 *   Always keep a plain `text` variant for fallback targets.
 *
 * Examples:
 * - `copy({ text: 'https://example.com' })`
 * - `copy({ text: '<strong>Hello</strong>', html: '<strong>Hello</strong>' })`
 */
export const copy = async ({ text, html }: CopyOptions): Promise<void> => {
	try {
		if (html && window.isSecureContext && navigator.clipboard?.write && typeof ClipboardItem !== 'undefined') {
			await navigator.clipboard.write([
				new ClipboardItem({
					'text/plain': new Blob([text], { type: 'text/plain' }),
					'text/html': new Blob([html], { type: 'text/html' }),
				}),
			]);
			toast.success('Copied!');
			return;
		}

		if (window.isSecureContext && navigator.clipboard?.writeText) {
			await navigator.clipboard.writeText(text);
			toast.success('Copied!');
			return;
		}
	} catch {
		// fall through to legacy implementation
	}

	if (copyLegacy(text)) {
		toast.success('Copied!');
	} else {
		toast.error('Oops, unable to copy');
	}
};
